import { ApiBoardImageLoad } from "@/api";
import { canv } from "@/canv/global";
import {
    isImageFormatValid,
    isImageResolutionValid,
    isImageSizeValid,
    prepareImage,
} from "@/canv/utils";
import {
    IMAGE_FORMATS_READABLE,
    MAX_IMAGE_DOWNLOAD_COUNT,
    MAX_IMAGE_HEIGHT,
    MAX_IMAGE_SIZE_READABLE,
    MAX_IMAGE_WIDTH,
} from "@/constants";
import { DocumentViewerWidget } from "@/elements/documentViewer";
import { GifWidget } from "@/elements/gif";
import { ImageWidget } from "@/elements/image";
import { SNACKBAR_TIME } from "@/elements/text/constants";
import { Group } from "@/group";
import { t } from "@/i18n";
import { showSnackbar } from "@/snackbar/snackbar";
import { uploadImageLimit } from "../canvasController";
import { getViewport } from "./common";

type Position = "center" | { x: number; y: number };

const isIterable = <T = unknown>(v): v is Iterable<T> => typeof v[Symbol.iterator] === "function";

export function loadFiles(blobsOrBlob: Blob | Iterable<Blob>, position?: Position) {
    if (canv.isBoardModeView()) return;

    const files: Blob[] = isIterable(blobsOrBlob) ? Array.from(blobsOrBlob) : [blobsOrBlob];

    if (files.length > MAX_IMAGE_DOWNLOAD_COUNT) {
        showSnackbar(t("media.countLimit", { count: MAX_IMAGE_DOWNLOAD_COUNT }), {
            duration: SNACKBAR_TIME,
            type: "warning",
        });
        return Promise.resolve();
    }

    const MOVE_DISTANCE = 50;

    return new Promise((resolve) => {
        const cursorPoint = canv.actionManager.getActionPoint();
        const viewport = getViewport();

        const preparePromises: Promise<unknown>[] = [];
        const loadPromises: Promise<unknown>[] = [];

        files.forEach((blob, index) => {
            if (!isImageSizeValid(blob.size)) {
                showSnackbar(t("media.sizeLimit", { size: MAX_IMAGE_SIZE_READABLE }), {
                    duration: SNACKBAR_TIME,
                    type: "warning",
                });

                return;
            }
            if (!isImageFormatValid(blob.type)) {
                showSnackbar(t("media.formats", { formats: IMAGE_FORMATS_READABLE.join(", ") }), {
                    duration: SNACKBAR_TIME,
                    type: "warning",
                });

                return;
            }

            preparePromises.push(
                prepareImage(blob).then(async (img) => {
                    const isPdf = blob.type === "application/pdf";

                    if (!isPdf) {
                        if (!img) return;

                        if (!isImageResolutionValid(img.width, img.height)) {
                            showSnackbar(
                                t("media.resolution", {
                                    width: MAX_IMAGE_WIDTH,
                                    height: MAX_IMAGE_HEIGHT,
                                }),
                                {
                                    duration: SNACKBAR_TIME,
                                    type: "warning",
                                },
                            );

                            return;
                        }
                    }

                    const isGif = blob.type === "image/gif";

                    let newElement: GifWidget | ImageWidget | DocumentViewerWidget | null = null;

                    if (isGif) {
                        newElement = new GifWidget(img.src, img.width, img.height, true);
                    } else if (isPdf) {
                        newElement = new DocumentViewerWidget((blob as File).name, 80, 103, blob);
                    } else {
                        newElement = new ImageWidget(img.src, img.width, img.height);
                    }

                    newElement?.setLoading(true);
                    const newGroup = new Group(true);
                    newElement?.setParent(newGroup);

                    if (position === "center") {
                        const positionX =
                            viewport.viewportStartX +
                            viewport.viewportWidth / 2 +
                            index * MOVE_DISTANCE * canv.dpr;
                        const positionY =
                            viewport.viewportStartY +
                            viewport.viewportHeight / 2 +
                            index * MOVE_DISTANCE * canv.dpr;
                        newGroup.translateGroup(positionX, positionY);
                    } else if (position?.x && position?.y) {
                        const positionX =
                            viewport.viewportStartX +
                            (position.x / canv.m[0]) * canv.dpr +
                            index * MOVE_DISTANCE * canv.dpr;
                        const positionY =
                            viewport.viewportStartY +
                            (position.y / canv.m[0]) * canv.dpr +
                            index * MOVE_DISTANCE * canv.dpr;
                        newGroup.translateGroup(positionX, positionY);
                    } else {
                        const positionX =
                            cursorPoint.x -
                            newGroup.startX -
                            newGroup.width / 2 +
                            index * MOVE_DISTANCE * canv.dpr;
                        const positionY =
                            cursorPoint.y -
                            newGroup.startY -
                            newGroup.height / 2 +
                            index * MOVE_DISTANCE * canv.dpr;
                        newGroup.translateGroup(positionX, positionY);
                    }
                    canv.canvasController.addGroup(newGroup);
                    canv.redraw();

                    loadPromises.push(
                        uploadImageLimit(() => {
                            return ApiBoardImageLoad(blob)
                                .then((json) => {
                                    newElement?.initImage(json.path, undefined, true);
                                    newElement?.setLoading(false);
                                    canv.widgetMenu.reopen();

                                    canv.eventManager.openRecord();
                                    canv.eventManager.createBuildEvent("create", newGroup);
                                    canv.eventManager.closeRecord();

                                    return newGroup;
                                })
                                .catch((err) => {
                                    showSnackbar(err.toString(), {
                                        duration: SNACKBAR_TIME,
                                        type: "warning",
                                    });
                                    console.log("ошибка загрузки картинки", err);
                                    canv.canvasController.deleteProcessor(newGroup, true);
                                    return;
                                });
                        }),
                    );
                }),
            );
        });

        return Promise.all(preparePromises)
            .then(() => Promise.all(loadPromises))
            .then(resolve);
    });
}
